import axios from 'axios';
import { toast } from 'react-toastify';

let sessionExpiredCallback = null;

// Throttle the rate-limit toast so a burst of 429s shows one message, not dozens.
let lastRateLimitToastAt = 0;
const RATE_LIMIT_TOAST_INTERVAL_MS = 15000;

let globalCsrfToken = null;

export const setSessionExpiredCallback = (callback) => {
  sessionExpiredCallback = callback;
};

export const setGlobalCsrfToken = (token) => {
  globalCsrfToken = token;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true
});


api.interceptors.request.use(
  (config) => {
    if (globalCsrfToken) {
      config.headers['X-XSRF-TOKEN'] = globalCsrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Endpoints where a 401 is part of a normal unauthenticated flow (login,
// logout, password reset). A 401 here must NOT trigger the session-expired
// modal; everywhere else a 401 means the active session expired.
const SESSION_EXPIRY_EXEMPT_URLS = [
  '/auth/login',
  '/auth/logout',
  'auth/check-org',
  'forgot/',
  '/auth/saml',
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = String(error?.config?.url || '');
    const isExempt = SESSION_EXPIRY_EXEMPT_URLS.some((u) => requestUrl.includes(u));
    const shouldTriggerSessionExpired = status === 401 && !isExempt;

    if (shouldTriggerSessionExpired && typeof sessionExpiredCallback === 'function') {
      sessionExpiredCallback();
    }

    // 429 = server rate limit. It is transient and must NOT be treated like an
    // auth failure; just tell the user once and let them retry.
    if (status === 429) {
      const now = Date.now();
      if (now - lastRateLimitToastAt > RATE_LIMIT_TOAST_INTERVAL_MS) {
        lastRateLimitToastAt = now;
        toast.warn('Too many requests - please wait a few seconds and try again.');
      }
    }
    return Promise.reject(error);
  }
);

export default api;
